import { ref, computed } from 'vue';
import { useAuthentication } from '@/composables/useAuthentication';
import { MEMBER_TYPES, MemberType, isMemberType } from '@/enums/member-types';

let initializeAccountLevelsMaximumLimitsPromise: Promise<void> | null = null;
const accountLevelsMaximumLimits = ref<AccountLevelsMaximumLimits | undefined>();

const isAccountLevelsMaximumLimitsInitialized = computed(() => {
  return !!accountLevelsMaximumLimits.value;
});

const autoRefreshIntervalSeconds = computed(() => {
  const { memberType } = useAuthentication();

  return isAccountLevelsMaximumLimitsInitialized.value && accountLevelsMaximumLimits.value ? accountLevelsMaximumLimits.value[memberType.value].autoRefreshIntervalSec : 15;
});

const maximumSharpChartWidthPixelsAllowed = computed(() => {
  const { memberType } = useAuthentication();

  return isAccountLevelsMaximumLimitsInitialized.value && accountLevelsMaximumLimits.value ? accountLevelsMaximumLimits.value[memberType.value].maxSharpChartWidthPx : -1;
});

const maximumNumberOfChartListsAllowed = computed(() => {
  const { memberType } = useAuthentication();

  return isAccountLevelsMaximumLimitsInitialized.value && accountLevelsMaximumLimits.value ? accountLevelsMaximumLimits.value[memberType.value].maxChartLists : -1;
});

const maximumNumberOfChartsPerChartListAllowed = computed(() => {
  const { memberType } = useAuthentication();

  return isAccountLevelsMaximumLimitsInitialized.value && accountLevelsMaximumLimits.value ? accountLevelsMaximumLimits.value[memberType.value].maxChartsPerChartList : -1;
});

const maximumNumberOfChartStylesAllowed = computed(() => {
  const { memberType } = useAuthentication();

  return isAccountLevelsMaximumLimitsInitialized.value && accountLevelsMaximumLimits.value ? accountLevelsMaximumLimits.value[memberType.value].maxChartStyles : -1;
});

const maximumNumberOfPriceAlertsAllowed = computed(() => {
  const { memberType } = useAuthentication();

  return isAccountLevelsMaximumLimitsInitialized.value && accountLevelsMaximumLimits.value ? accountLevelsMaximumLimits.value[memberType.value].maxPriceAlerts : -1;
});

interface AccountLevelMaximumLimits {
  autoRefreshIntervalSec: number;
  maxSharpChartWidthPx: number;
  maxChartLists: number;
  maxChartsPerChartList: number;
  maxChartStyles: number;
  maxPriceAlerts: number;
};

function isAccountLevelMaximumLimits(unknownValue: unknown): unknownValue is AccountLevelMaximumLimits {
  return (
    typeof unknownValue === 'object' &&
    unknownValue !== null &&
    'autoRefreshIntervalSec' in unknownValue &&
    'maxSharpChartWidthPx' in unknownValue &&
    'maxChartLists' in unknownValue &&
    'maxChartsPerChartList' in unknownValue &&
    'maxChartStyles' in unknownValue &&
    'maxPriceAlerts' in unknownValue &&
    typeof unknownValue.autoRefreshIntervalSec === 'number' &&
    typeof unknownValue.maxSharpChartWidthPx === 'number' &&
    typeof unknownValue.maxChartLists === 'number' &&
    typeof unknownValue.maxChartsPerChartList === 'number' &&
    typeof unknownValue.maxChartStyles === 'number' &&
    typeof unknownValue.maxPriceAlerts === 'number'
  );
}

type AccountLevelsMaximumLimits = {
  [value in MEMBER_TYPES]: AccountLevelMaximumLimits;
};

function isAccountLevelsMaximumLimits(unknownValue: unknown): unknownValue is AccountLevelsMaximumLimits {
  if (typeof unknownValue !== 'object' || unknownValue === null) {
    return false;
  }

  const memberTypeInformation: Partial<Record<MemberType, unknown>> = {};

  for (const [key, value] of Object.entries(unknownValue)) {
    if (isMemberType(key)) {
      memberTypeInformation[key] = value;
    }
  }

  return Object.values(MEMBER_TYPES).every((memberType: MemberType) => isAccountLevelMaximumLimits(memberTypeInformation[memberType]));
}

async function fetchAccountLevelsMaximumLimits(): Promise<AccountLevelsMaximumLimits> {
  let responseJson;

  try {
    const response = await fetch('//d.stockcharts.com/memberTypes.json', {
      method: 'GET'
    });

    responseJson = await response.json();

    window.sc3WorkbenchLogger.info('account levels maximum limits responseJson', responseJson);
  } catch (e) {
    window.sc3WorkbenchLogger.error('Failed to fetch account levels maximum limits.', e);

    throw new Error('Failed to fetch account levels maximum limits.', { cause: e });
  }

  if (!isAccountLevelsMaximumLimits(responseJson)) {
    throw new Error(`Failed to fetch account levels maximum limits - responseJson does not match AccountLevelsMaximumLimits type: ${typeof responseJson} ${responseJson}.`);
  }

  return responseJson;
}

async function _initializeAccountLevelsMaximumLimits(): Promise<void> {
  if (isAccountLevelsMaximumLimitsInitialized.value) {
    return;
  }

  try {
    accountLevelsMaximumLimits.value = await fetchAccountLevelsMaximumLimits();
  } catch (e) {
    window.sc3WorkbenchLogger.error('Failed to initialize account levels maximum limits.', e);
  }
}

async function initializeAccountLevelsMaximumLimits(): Promise<void> {
  let promiseObject;

  if (initializeAccountLevelsMaximumLimitsPromise) {
    // there's already a request to initialize the account levels maximum limits underway
    // - await the current request's promise rather than kicking off a new request
    promiseObject = initializeAccountLevelsMaximumLimitsPromise;
  } else {
    promiseObject = _initializeAccountLevelsMaximumLimits();

    // store promise so that additional initializeAccountLevelsMaximumLimits function
    // calls that are made while the current network call is still underway can
    // await this promise rather than inefficiently making their own duplicate
    // network requests for the same data
    initializeAccountLevelsMaximumLimitsPromise = promiseObject;

    promiseObject.then(() => {
      // remove the now-resolved promise from the cache
      initializeAccountLevelsMaximumLimitsPromise = null;
    });
  }

  await promiseObject;
}

export function useAccountLevelsMaximumLimits() {
  return {
    isAccountLevelsMaximumLimitsInitialized,
    autoRefreshIntervalSeconds,
    maximumSharpChartWidthPixelsAllowed,
    maximumNumberOfChartListsAllowed,
    maximumNumberOfChartsPerChartListAllowed,
    maximumNumberOfChartStylesAllowed,
    maximumNumberOfPriceAlertsAllowed,
    initializeAccountLevelsMaximumLimits
  };
}
